import styled from "@emotion/styled"
import { Link } from "utils"
import { breakpoints, layoutPadding } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/** Emotion doesn't auto-strip $-prefixed props like styled-components.
 *  Use this on every styled(CustomComponent) that receives transient props. */
const skipTransient = { shouldForwardProp: (p: string) => !p.startsWith("$") }

/* ------------------------------------------------------------------ */
/* Height constants — exported for Layout.tsx padding-top              */
/* ------------------------------------------------------------------ */
// EGDE-311: mobile top padding raised 16 -> 48 to match desktop (and the
// ~49px Figma gap); the mobile header was sitting too tight to the top edge.
export const HEADER_HEIGHT_MOBILE = 133 // 48 padding-top + 28 link-bar + 1 divider + 56 main-bar
export const HEADER_HEIGHT_DESKTOP = 187 // 48 padding-top + 34 link-bar + 16 link-bar-mb + 1 divider + 16 divider-mb + 56 main-bar + 16 padding-bottom
export const HEADER_HOMEPAGE_HEIGHT_MOBILE = 133
export const HEADER_HOMEPAGE_HEIGHT_DESKTOP = 171 // 48 padding-top + 34 link-bar + 16 link-bar-mb + 1 divider + 16 divider-mb + 56 main-bar

/* ------------------------------------------------------------------ */
/* Inner wrapper — replaces Container for full-width divider support   */
/* ------------------------------------------------------------------ */
export const HeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: ${breakpoints.lg}px;
    padding: 0 ${layoutPadding.desktop}px;
    margin: 0 auto;
  }

  @media (min-width: ${breakpoints.xl}px) {
    max-width: ${breakpoints.xl}px;
    padding: 0 ${layoutPadding.desktopL}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    max-width: ${breakpoints.xxl}px;
    padding: 0 ${layoutPadding.desktopXl}px;
  }
`

/* ------------------------------------------------------------------ */
/* Link Header — top bar with service tabs                             */
/* ------------------------------------------------------------------ */
export const LinkHeaderBar = styled("div", skipTransient)<{
  $isHomepage: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${sizes.spacing[24]}px;
  padding: 0 ${layoutPadding.mobile}px;
  height: 28px;
  width: 100%;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: 0;
    gap: ${sizes.spacing[32]}px;
    height: 34px;
    margin-bottom: 16px;
  }
`

/* ------------------------------------------------------------------ */
/* Full-width divider below links — spans entire viewport              */
/* ------------------------------------------------------------------ */
export const LinkDivider = styled("div", skipTransient)<{
  $isHomepage: boolean
}>`
  width: 100%;
  height: 1px;
  /* EGDE-495: the homepage design has no divider line under the service
     tabs — only the active tab's own underline. Inner pages keep the
     subtle gray rule on the white header. */
  background: ${(p) =>
    p.$isHomepage ? "transparent" : colors.primitives.neutral200};
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    margin-bottom: ${sizes.spacing[16]}px;
  }
`

export const ServiceTabLink = styled(Link, skipTransient)<{
  $isHomepage: boolean
  $isActive?: boolean
}>`
  text-decoration: none;
  color: ${(p) =>
    p.$isHomepage ? colors.primitives.neutral0 : colors.primitives.neutral600};
  font-size: 14px;
  font-weight: ${(p) => (p.$isActive ? 700 : 500)};
  line-height: 20px;
  white-space: nowrap;
  /* EGDE-495: the active tab underline is brand orange and hugs the text —
     no padding gap between the label and its line. */
  padding-bottom: 0;
  border-bottom: 2px solid
    ${(p) => (p.$isActive ? colors.primitives.primary500 : "transparent")};
  transition: border-color 0.2s;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
    line-height: 26px;
  }

  &:hover {
    border-bottom-color: ${colors.primitives.primary500};
  }
`

/* ------------------------------------------------------------------ */
/* Full Header Wrapper — outer container for full header state         */
/* ------------------------------------------------------------------ */
export const HeaderWrapper = styled("header", skipTransient)<{
  $isHomepage: boolean
  $isVisible: boolean
}>`
  position: ${(p) => (p.$isHomepage ? "absolute" : "fixed")};
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  /* EGDE-311: mobile was only 16px, leaving the header cramped against the top
     of the viewport. Match the desktop 48px so there is a proper gap above the
     top link bar on mobile too. */
  padding-top: ${sizes.spacing[48]}px;
  background: ${(p) =>
    p.$isHomepage ? "transparent" : colors.primitives.neutral0};
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
  pointer-events: ${(p) => (p.$isVisible ? "auto" : "none")};
  transition: opacity 0.3s ease;

  ${(p) =>
    p.$isHomepage &&
    `
    overflow: visible;

    & > * {
      position: relative;
      z-index: 1;
    }

    /* EGDE-493: black mask matches the Figma gradient layer exactly —
       80% black at the top easing through 50% at the middle to fully
       transparent at the bottom edge of the header. */
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 100%;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.8) 0%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0) 100%
      );
      pointer-events: none;
      z-index: 0;
    }`}

  @media (min-width: ${breakpoints.lg}px) {
    padding-top: 48px;
    padding-bottom: ${(p) => (p.$isHomepage ? "0" : "16px")};
  }
`

/* ------------------------------------------------------------------ */
/* Main Header Bar — CTA, Logo, Icons row                              */
/* ------------------------------------------------------------------ */
export const MainHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  height: 56px;
  padding: 0 ${layoutPadding.mobile}px;

  @media (min-width: ${breakpoints.lg}px) {
    padding: 0;
  }
`

/* ------------------------------------------------------------------ */
/* Logo                                                                 */
/* ------------------------------------------------------------------ */
export const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;

  /* EGDE-293: on mobile the logo is a normal in-flow flex item so the CTA and
     icon group can never paint over it (flex items don't overlap). Only on
     desktop, where there is ample room, is it absolutely centered. */
  @media (min-width: ${breakpoints.lg}px) {
    position: absolute;
    /* @noflip */
    left: 50%;
    /* @noflip */
    transform: translateX(-50%);
  }
`

export const LogoImageWrapper = styled.div`
  position: relative;
  width: 120px;
  height: 42px;
  overflow: hidden;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    width: 162px;
    height: 56px;
  }
`

export const LogoImage = styled("img", skipTransient)<{
  $isHomepage?: boolean
}>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  ${(p) => p.$isHomepage && "filter: brightness(0) invert(1);"}
  transition: filter 0.3s ease;
`

/* ------------------------------------------------------------------ */
/* Icon group (search + hamburger) — right side visually               */
/* In RTL flex-row, these come FIRST in JSX order                      */
/* ------------------------------------------------------------------ */
export const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[12]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[16]}px;
  }
`

export const IconButton = styled("button", skipTransient)<{
  $isHomepage?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  color: ${(p) =>
    p.$isHomepage ? colors.primitives.neutral0 : colors.primitives.neutral600};
  transition: opacity 0.2s;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.7;
  }
`

/* ------------------------------------------------------------------ */
/* CTA Button — secondary style with gradient border                   */
/* ------------------------------------------------------------------ */
export const CtaButton = styled(Link, skipTransient)<{
  $isHomepage?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  /* EGDE-293: tighter mobile padding so the CTA, centered logo and icon group
     all fit on one row at 375px without colliding. Desktop padding restored in
     the lg media query below. */
  padding: 0 ${sizes.spacing[16]}px;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  /* EGDE-355: gradient↔solid backgrounds are not interpolable — only animate
     color; the background swap on hover is instant. */
  transition: color 0.2s ease-in-out;
  position: relative;
  color: ${(p) =>
    p.$isHomepage ? colors.primitives.neutral0 : colors.primitives.neutral600};
  /* EGDE-495: homepage fill is the Figma "50% Black" CTA color variant */
  background: ${(p) =>
    p.$isHomepage ? "rgba(0,0,0,0.5)" : colors.primitives.neutral0};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px;
    background: ${colors.gradients.gradientCta};
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    height: 44px;
    padding: 0 ${sizes.spacing[32]}px;
    font-size: 16px;
  }

  /* EGDE-495: hover fills the pill with the CTA gradient per the Figma
     Main CTA / Secondary / Hover state; text flips to the dark primary. */
  &:hover {
    background: ${colors.gradients.gradientCta};
    color: ${colors.light.text.primary};
  }
`

/* CTA Button (button variant) — opens the contact modal; same styling as CtaButton, rendered as a <button> */
export const CtaButtonAsButton = CtaButton.withComponent("button")

/* ------------------------------------------------------------------ */
/* Scrolling Pill — compact floating header on scroll                  */
/* ------------------------------------------------------------------ */
export const ScrollingPillWrapper = styled("div", skipTransient)<{
  $isVisible: boolean
}>`
  position: fixed;
  top: ${sizes.spacing[16]}px;
  z-index: 1002;
  /* @noflip */
  left: 50%;
  /* @noflip */
  transform: translateX(-50%)
    ${(p) => (p.$isVisible ? "translateY(0)" : "translateY(-20px)")};
  /* EGDE-456: a fixed element with left:50% and no width shrink-fits into the
     space between 50% and the viewport edge — only ~50vw. On phones that is
     narrower than the pill's content, so the white pill collapsed and the CTA
     and hamburger stuck out past its rounded edges with no padding.
     max-content sizes the wrapper to the pill itself; the viewport cap keeps
     breathing room on both sides on the narrowest screens. */
  width: max-content;
  max-width: calc(100% - ${sizes.spacing[16] * 2}px);
  opacity: ${(p) => (p.$isVisible ? 1 : 0)};
  pointer-events: ${(p) => (p.$isVisible ? "auto" : "none")};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
`

export const ScrollingPillContainer = styled.div`
  display: flex;
  align-items: center;
  /* EGDE-456: tighter gap/padding below the desktop breakpoint so the pill
     fits inside the wrapper's viewport cap on small phones. */
  gap: ${sizes.spacing[8]}px;
  background: ${colors.primitives.neutral0};
  border-radius: ${sizes.borderRadius.full}px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.16);
  padding: ${sizes.spacing[12]}px;
  min-width: 0;
  max-width: 100%;
  width: auto;
  height: 62px;
  justify-content: center;

  @media (min-width: ${breakpoints.lg}px) {
    gap: ${sizes.spacing[12]}px;
    min-width: 240px;
    height: 70px;
    padding: ${sizes.spacing[12]}px ${sizes.spacing[24]}px;
  }
`

export const PillSeparator = styled.div`
  width: 1px;
  height: 32px;
  background: ${colors.primitives.neutral200};
  flex-shrink: 0;
`

export const PillCtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 ${sizes.spacing[16]}px;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  font-family: inherit;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  color: ${colors.primitives.neutral600};
  background: ${colors.primitives.neutral0};
  position: relative;
  /* EGDE-355: gradient↔solid backgrounds are not interpolable — only animate
     color; the background swap on hover is instant. */
  transition: color 0.2s ease-in-out;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px;
    background: ${colors.gradients.gradientCta};
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 14px;
    padding: 0 ${sizes.spacing[24]}px;
  }

  /* EGDE-495: same hover as the main header CTA — the pill fills with the
     CTA gradient per the Figma Main CTA Hover state. */
  &:hover {
    background: ${colors.gradients.gradientCta};
    color: ${colors.light.text.primary};
  }
`

/* Pill CTA (button variant) — opens the contact modal; same styling as PillCtaButton, rendered as a <button> */
export const PillCtaButtonAsButton = PillCtaButton.withComponent("button")

/* EGDE-484: the pill logo navigates home, same as the main header logo. */
export const PillLogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`

export const PillLogoIcon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    width: 40px;
    height: 40px;
  }
`

export const PillHamburgerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${colors.primitives.neutral600};

  svg {
    width: 18px;
    height: 12px;
  }

  &:hover {
    opacity: 0.7;
  }
`
